import React, { createContext, useContext, useState, useEffect } from 'react';
import { config } from '../config/environment';
import CognitoAuthService, { CognitoUser, CognitoChallenge } from '../services/cognitoAuthService';
import managementService, {
  ManagementLookupOutcome,
  ManagementUser,
  Shift,
} from '../services/managementService';

interface User {
  id: string;
  username: string;
  email: string;
  isAuthenticated: boolean;
  accessToken?: string;
  managementUser?: ManagementUser;
  /**
   * feat-324 — outcome of the management-user lookup that produced `managementUser`.
   * 'failed' makes the route guards show "We couldn't load your account" (Try again)
   * instead of "Access denied". Optional: absent is treated as a definitive answer.
   */
  roleLookup?: ManagementLookupOutcome;
  shifts?: Shift[];
  tenantContext?: {
    email: string;
    role: string;
    tenantId?: string;
    locationId?: string;
  };
}

interface LoginResult {
  success: boolean;
  challenge?: CognitoChallenge;
  // feat-191-evaluator-role (C2): the management role resolved during THIS login,
  // echoed back so the caller can pick a landing page (landingPathForRole).
  // LoginPage/PasswordChangePage navigate inside the same async continuation that
  // called login(), where their captured `user` from useAuth() is still the
  // pre-login value — reading the role off context there would be a stale-closure
  // race. This is a read-only echo of data login() already fetched; it grants
  // nothing and no gate consumes it.
  role?: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * feat-300 — `useAuth` without the throw: `undefined` outside an AuthProvider.
 * For surfaces that only DECORATE with the user (e.g. "You" vs an email on a
 * thread message) and are also rendered by tests without a provider.
 */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cognitoService] = useState(() => new CognitoAuthService(config.cognito));

  // Function to fetch management user data
  // feat-324: also returns WHY there is (or isn't) a user, so the guards can tell a
  // failed lookup from a definitive denial.
  const fetchManagementUser = async (
    email: string,
  ): Promise<{ managementUser: ManagementUser | null; roleLookup: ManagementLookupOutcome }> => {
    try {
      console.log('AuthContext - Fetching management user for email:', email);
      const response = await managementService.getManagementUserByEmail(email);
      console.log('AuthContext - Management user response:', response);
      if (response.success && response.user) {
        console.log('AuthContext - Management user found:', response.user);
        return { managementUser: response.user, roleLookup: 'ok' };
      }
      console.warn('AuthContext - Management user not found or failed:', response.error);
      // A missing outcome on a non-success response is unclassified — treat as failed.
      return { managementUser: null, roleLookup: response.outcome ?? 'failed' };
    } catch (error) {
      console.error('AuthContext - Error fetching management user:', error);
      return { managementUser: null, roleLookup: 'failed' };
    }
  };

  // Function to fetch shifts for a manager
  const fetchManagerShifts = async (managerId: string): Promise<Shift[]> => {
    try {
      const response = await managementService.getShiftsByManagerId(managerId);
      if (response.success && response.shifts) {
        return response.shifts;
      }
      return [];
    } catch (error) {
      console.error('Error fetching shifts:', error);
      return [];
    }
  };

  // Dummy user credentials - used only in dev environment
  const dummyUsers = [
    { email: 'admin@bespoke.ai', password: 'password123', username: 'admin' },
    { email: 'manager@bespoke.ai', password: 'manager123', username: 'manager' },
    { email: 'user@bespoke.ai', password: 'user123', username: 'user' }
  ];

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (config.useDummyData) {
          // Use dummy authentication for dev environment
          const savedUser = localStorage.getItem('user');
          if (savedUser) {
            setUser(JSON.parse(savedUser));
          }
        } else {
          // Use Cognito authentication for dev/prod
          const cognitoUser = await cognitoService.getCurrentUser();
          console.log('AuthContext - Cognito user:', cognitoUser);
          console.log('AuthContext - Environment:', config.environment);
          if (cognitoUser) {
            // Fetch management user data and shifts for all environments (dev, fake, local, prod)
            console.log('AuthContext - Fetching management user for environment:', config.environment);
            const { managementUser, roleLookup } = await fetchManagementUser(cognitoUser.email);
            let shifts: Shift[] = [];
            
            // If management user found, fetch their shifts
            if (managementUser) {
              shifts = await fetchManagerShifts(managementUser.id);
            }
            
            const userWithManagement: User = {
              ...cognitoUser,
              managementUser: managementUser || undefined,
              roleLookup,
              shifts: shifts,
              tenantContext: managementUser && managementUser.role ? {
                email: managementUser.email,
                role: managementUser.role.charAt(0).toUpperCase() + managementUser.role.slice(1).toLowerCase(),
                tenantId: managementUser.tenant_id,
                locationId: managementUser.location_id
              } : undefined
            };
            console.log('AuthContext - Setting user with management data:', userWithManagement);
            setUser(userWithManagement);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [cognitoService]);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      if (config.useDummyData) {
        // Use dummy authentication for dev environment
        const foundUser = dummyUsers.find(
          u => u.email === email && u.password === password
        );

        if (foundUser) {
          // Create management user based on username
          let managementUser: ManagementUser | undefined;
          if (foundUser.username === 'admin') {
            managementUser = {
              id: 'admin-1',
              email: foundUser.email,
              first_name: 'Admin',
              last_name: 'User',
              role: 'Admin',
              tenant_id: undefined,
              location_id: undefined
            };
          } else if (foundUser.username === 'manager') {
            managementUser = {
              id: 'manager-1',
              email: foundUser.email,
              first_name: 'Store',
              last_name: 'Manager',
              role: 'Manager',
              tenant_id: 'tenant-1',
              location_id: 'location-1'
            };
          }

          const userData: User = {
            id: foundUser.username,
            username: foundUser.username,
            email: foundUser.email,
            isAuthenticated: true,
            accessToken: 'dummy-token',
            managementUser: managementUser,
            // feat-324: dummy mode has no lookup to fail.
            roleLookup: managementUser ? 'ok' : 'not_found',
            tenantContext: managementUser && managementUser.role ? {
              email: managementUser.email,
              role: managementUser.role.charAt(0).toUpperCase() + managementUser.role.slice(1).toLowerCase(),
              tenantId: managementUser.tenant_id,
              locationId: managementUser.location_id
            } : undefined
          };
          
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          return { success: true, role: managementUser?.role };
        }
        
        return { success: false };
      } else {
        // Use Cognito authentication for dev/prod
        const result = await cognitoService.signIn(email, password);
        
        // Check if there's a password change challenge
        if (result.challenge) {
          return {
            success: false,
            challenge: result.challenge
          };
        }
        
        if (result.user) {
          // Fetch management user data and shifts for all environments (dev, fake, local, prod)
          const { managementUser, roleLookup } = await fetchManagementUser(email);
          let shifts: Shift[] = [];
          
          // If management user found, fetch their shifts
          if (managementUser) {
            shifts = await fetchManagerShifts(managementUser.id);
          }
          
          const userWithManagement: User = {
            ...result.user,
            managementUser: managementUser || undefined,
            roleLookup,
            shifts: shifts,
            tenantContext: managementUser && managementUser.role ? {
              email: managementUser.email,
              role: managementUser.role.charAt(0).toUpperCase() + managementUser.role.slice(1).toLowerCase(),
              tenantId: managementUser.tenant_id,
              locationId: managementUser.location_id
            } : undefined
          };
          setUser(userWithManagement);
          return { success: true, role: managementUser?.role };
        }
        return { success: false };
      }
    } catch (error) {
      console.error('Login error:', error);
      // Re-throw the error so it can be handled by the LoginPage
      throw error;
    }
  };


  const logout = async (): Promise<void> => {
    try {
      if (!config.useDummyData) {
        await cognitoService.signOut();
      } else {
        localStorage.removeItem('user');
      }
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if Cognito logout fails
      setUser(null);
      if (config.useDummyData) {
        localStorage.removeItem('user');
      }
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
